import languages from "@/data/languages.json";
import { resolveLanguageCodeFromParam } from "@/lib/language-pair-resolver";

const aliases: Record<string, string> = {
  english: "en-US",
  spanish: "es-ES",
  portuguese: "pt-PT",
  chinese: "zh-CN",
  mandarin: "zh-CN",
  cantonese: "yue-HK",
  arabic: "ar",
};

/** Use the course locale, preserving regional variants; unknown names stay unknown. */
export function getFlashcardLocale(language: string): string | undefined {
  const value = language.trim();
  const code =
    resolveLanguageCodeFromParam(value, languages) ||
    aliases[value.toLowerCase()] ||
    (/^[a-z]{2,3}(?:-[a-z0-9]{2,8})*$/i.test(value) ? value : undefined);
  if (!code) return undefined;
  const speechCode = code === "cmn-CN" ? "zh-CN" : code === "ar-XA" ? "ar" : code;
  try {
    return Intl.getCanonicalLocales(speechCode)[0];
  } catch {
    return undefined;
  }
}

export function stopFlashcardPronunciation(): void {
  if (typeof window !== "undefined" && "speechSynthesis" in window) window.speechSynthesis.cancel();
}

/** Never silently read an unsupported target language using an English voice. */
export function speakFlashcardText(
  text: string,
  language: string,
  onError: (message: string) => void
): void {
  const locale = getFlashcardLocale(language);
  const unavailable = `Pronunciation is unavailable for ${language} on this device.`;
  if (
    !locale ||
    !("speechSynthesis" in window) ||
    typeof SpeechSynthesisUtterance === "undefined"
  ) {
    onError(unavailable);
    return;
  }
  const synth = window.speechSynthesis;
  const voices = synth.getVoices();
  const voiceLocale = (voice: SpeechSynthesisVoice) => voice.lang.replace(/_/g, "-").toLowerCase();
  const normalized = locale.toLowerCase();
  const voice =
    voices.find((candidate) => voiceLocale(candidate) === normalized) ||
    voices.find((candidate) => voiceLocale(candidate).split("-")[0] === normalized.split("-")[0]);
  if (!voice) {
    onError(
      voices.length
        ? unavailable
        : "Pronunciation voices are still loading or unavailable. Please try again."
    );
    return;
  }
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = locale;
  utterance.voice = voice;
  utterance.onerror = (event) => {
    if (event.error !== "interrupted" && event.error !== "canceled") onError(unavailable);
  };
  try {
    synth.cancel();
    synth.speak(utterance);
  } catch {
    onError(unavailable);
  }
}
